
import React, { useEffect, useRef, useState } from 'react';
import { LocalAudioTrack } from 'livekit-client';
import './VisualizerOverrides.css';
import {
    VISUALIZER_BAR_COUNT,
    VISUALIZER_BAR_WIDTH,
    VISUALIZER_GAP,
    VISUALIZER_BORDER_RADIUS,
    VISUALIZER_MIN_HEIGHT_PX,
    VISUALIZER_HEIGHT,
    VISUALIZER_WIDTH,
} from './visualizerConstants';

interface PreJoinVisualizerProps {
    track: LocalAudioTrack;
    micEnabled?: boolean;
    barCount?: number;
    height?: number;
    width?: number;
    color?: string;
}

const THRESHOLD = 0.05; // Only update state when levels change by more than 5%

const PreJoinVisualizer: React.FC<PreJoinVisualizerProps> = ({
    track,
    micEnabled = true,
    barCount = VISUALIZER_BAR_COUNT,
    height = VISUALIZER_HEIGHT,
    width = VISUALIZER_WIDTH,
    color = 'white'
}) => {
    const [audioLevels, setAudioLevels] = useState<number[]>(new Array(barCount).fill(0));
    const analyzerRef = useRef<AnalyserNode | null>(null);
    const animationFrameRef = useRef<number | undefined>(undefined);
    const audioContextRef = useRef<AudioContext | null>(null);
    const prevLevelsRef = useRef<number[]>(new Array(barCount).fill(0));

    useEffect(() => {
        if (!track) return;
        if (!track.mediaStreamTrack) return;

        const setupAnalyzer = () => {
            try {
                const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
                if (audioContext.state === 'suspended') {
                    audioContext.resume();
                }
                // console.log('🔊 PreJoinVisualizer Debug:', {
                //     ctxState: audioContext.state,
                //     trackSid: track.sid,
                //     isMuted: track.isMuted,
                //     mediaStreamTrackEnabled: track.mediaStreamTrack?.enabled,
                //     mediaStreamTrackState: track.mediaStreamTrack?.readyState,
                //     mediaStreamTrackId: track.mediaStreamTrack?.id,
                //     hasMediaStreamTrack: !!track.mediaStreamTrack,
                //     trackType: typeof track,
                //     trackConstructor: track.constructor.name
                // });
                audioContextRef.current = audioContext;

                const analyzer = audioContext.createAnalyser();
                analyzer.fftSize = 32;
                analyzer.smoothingTimeConstant = 0.5;
                analyzerRef.current = analyzer;

                const source = audioContext.createMediaStreamSource(new MediaStream([track.mediaStreamTrack]));
                source.connect(analyzer);

                const dataArray = new Uint8Array(analyzer.frequencyBinCount);

                const update = () => {
                    if (!analyzerRef.current) return;

                    // When mic is off, show flat bars (no sound) — don't use analyzer data
                    const trackEnabled = track.mediaStreamTrack?.enabled !== false;
                    if (!micEnabled || !trackEnabled) {
                        const zeros = new Array(barCount).fill(0);
                        prevLevelsRef.current = zeros;
                        setAudioLevels(zeros);
                        animationFrameRef.current = requestAnimationFrame(update);
                        return;
                    }

                    analyzerRef.current.getByteFrequencyData(dataArray);

                    // We want a symmetric visualizer with the center bar being the most reactive (bass/voice)
                    // Since voice is mostly in the lower frequencies and we have 16 bins (fftSize=32):
                    // We'll extract 3 levels of intensity from low to high freq

                    // Level 1: Deep lows / Fundamental voice (Bins 0-1) - Center bar
                    const l1 = (dataArray[0] + dataArray[1]) / 2;

                    // Level 2: Body / Vowels (Bins 2-3) - Inner neighbors
                    const l2 = (dataArray[2] + dataArray[3]) / 2;

                    // Level 3: Clarity / Consonants (Bins 4-7) - Outer neighbors 
                    let sum3 = 0;
                    for (let k = 4; k < 8; k++) sum3 += dataArray[k];
                    const l3 = sum3 / 4;

                    // Normalize 0-255 to 0-1 with a boost
                    const normalize = (val: number) => Math.min(1, (val / 255) * 1.5);

                    const v1 = normalize(l1); // Center
                    const v2 = normalize(l2); // Mid
                    const v3 = normalize(l3); // Outer

                    // Layout: [Outer, Mid, Center, Mid, Outer]
                    const newLevels = [v3, v2, v1, v2, v3];
                    // Only update state when values change significantly to avoid update loops
                    const prev = prevLevelsRef.current;
                    const changed = newLevels.some((v, i) => Math.abs(v - prev[i]) > THRESHOLD);
                    if (changed) {
                        prevLevelsRef.current = newLevels;
                        setAudioLevels(newLevels);
                    }
                    animationFrameRef.current = requestAnimationFrame(update);
                };

                update();
            } catch (e) {
                console.error("Failed to setup visualizer", e);
            }
        };

        setupAnalyzer();

        return () => {
            if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
            if (audioContextRef.current) audioContextRef.current.close();
        };
    }, [track, barCount, micEnabled]);

    return (
        <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: `${width}px`,
            height: `${height}px`,
            gap: `${VISUALIZER_GAP}px`
        }}>
            {audioLevels.map((level, i) => (
                <div key={i} style={{
                    width: `${VISUALIZER_BAR_WIDTH}px`,
                    height: `${Math.max(VISUALIZER_MIN_HEIGHT_PX, level * height)}px`,
                    backgroundColor: 'white',
                    borderRadius: `${VISUALIZER_BORDER_RADIUS}px`,
                    transition: 'height 0.1s ease',
                    opacity: 0.8 + (level * 0.2)
                }} />
            ))}
        </div>
    );
};

export default PreJoinVisualizer;
